import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { first, map } from "rxjs/operators";
import { Circulation, IMetaData, IStepInit, Step } from "src/app/interfaces";
import { LogClass, Logger } from "src/app/logger";
import { CirculationMetadataService } from "src/app/services";
import { CirculationPermissions } from "../models";
@LogClass()
@Component({
    selector: "circ-information",
    templateUrl: "./information.component.html",
    styleUrls: ["./information.component.scss"],
    standalone: false
})
export class InformationComponent implements OnInit {
  @Input() circulation: Observable<Circulation>;
  @Input() steps: Observable<IStepInit[]>;
  @Input() permissions: Observable<CirculationPermissions>;

  @Output() cancelCirculation = new EventEmitter<Circulation>();
  @Output() startCirculation = new EventEmitter<Circulation>();
  @Output() editCirculation = new EventEmitter<Circulation>();
  @Output() copyCirculation = new EventEmitter<Circulation>();

  public visibleMetadata: Observable<IMetaData[]>;
  public disableStart: Observable<boolean>;
  public showStart: Observable<boolean>;
  public showEdit: Observable<boolean>;
  public showCancel: Observable<boolean>;
  public showCopy: Observable<boolean>;
  public showActionSeparator: Observable<boolean>;
  public dueDate: Observable<Date | null>;
  public owner: Observable<string[]>;
  private logger: Logger;

  constructor(private metadataService: CirculationMetadataService) {
    this.logger.debug("ctor");
  }

  public ngOnInit(): void {
    this.showStart = this.permissions.pipe(map((p) => p.canStart));
    this.showEdit = this.permissions.pipe(map((p) => p.canEdit));
    this.showCopy = this.permissions.pipe(map((p) => p.canCopy));
    this.showCancel = this.permissions.pipe(map((p) => p.canCancel));
    this.visibleMetadata = this.circulation.pipe(
      map((c) => this.metadataService.getVisibleMetadata(c))
    );
    this.owner = this.circulation.pipe(
      map((p) => p.props.owner.map((x) => x.login))
    );
    this.showActionSeparator = this.permissions.pipe(
      map((p) => p.canStart || p.canEdit || p.canCancel)
    );
    this.disableStart = combineLatest([
      this.steps.pipe(
        map((s) => {
          const noSteps = s.length < 1;
          const stepsWithErrors = s.some((t) => Step.getHasErrors(t));

          return noSteps || stepsWithErrors;
        })
      ),
      this.visibleMetadata.pipe(
        map((m) =>
          m.some((x) => {
            const isRequired = x.settings.isRequired;
            const hasValue = !!x.value;

            return isRequired && !hasValue;
          })
        )
      )
    ]).pipe(
      map(([stepError, metadataError]) => {
        return stepError || metadataError;
      })
    );
    this.dueDate = this.steps.pipe(
      map((s) => {
        if (s.length === 0) {
          return null;
        }

        let lastStep = s[0];
        for (const n of s) {
          if (n.stepNo > lastStep.stepNo && n.dueDate !== null) {
            lastStep = n;
          }
        }

        return lastStep.dueDate || null;
      })
    );
  }

  public emitStart() {
    this.circulation.pipe(first()).subscribe((c) => {
      this.startCirculation.emit(c);
    });
  }

  public emitCancel() {
    this.circulation.pipe(first()).subscribe((c) => {
      this.cancelCirculation.emit(c);
    });
  }

  public emitEdit() {
    this.circulation.pipe(first()).subscribe((c) => {
      this.editCirculation.emit(c);
    });
  }

  public emitCopy() {
    this.circulation.pipe(first()).subscribe((c) => {
      this.copyCirculation.emit(c);
    });
  }
}
